import { useState } from "react";
import { Link } from "wouter";
import { CalendarDays, MapPin, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useEvents } from "@/lib/queries";
import type { EventListStatus } from "@/lib/types";

// Org-scoped events list. Tabs filter upcoming / past / all. Tap a row to
// open the detail page where registration + roster live.
export default function EventsList() {
  const [tab, setTab] = useState<EventListStatus>("upcoming");
  const { data: events = [], isLoading } = useEvents({ status: tab });

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            Events
          </h1>
          <p className="text-muted-foreground text-sm">
            Leagues, tournaments, drop-ins. Register players, seed brackets,
            track results.
          </p>
        </div>
        <Button asChild size="sm">
          <Link href="/dashboard/events/new">
            <Plus size={14} /> New event
          </Link>
        </Button>
      </header>

      <div className="flex gap-2 text-xs font-display uppercase tracking-widest">
        {(["upcoming", "past", "all"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1 rounded-full border ${
              tab === t
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground text-sm">Loading events…</p>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-3">
            <CalendarDays
              className="mx-auto text-muted-foreground"
              size={32}
            />
            <p className="text-sm text-muted-foreground">
              No {tab === "all" ? "" : tab} events yet. Spin one up to start
              registering players.
            </p>
          </CardContent>
        </Card>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card overflow-hidden">
          {events.map((e) => (
            <li key={e.id}>
              <Link
                href={`/dashboard/events/${e.slug}`}
                className="flex items-center gap-3 p-3 hover:bg-muted/50"
              >
                <div className="shrink-0 w-12 text-center">
                  <DateChip date={e.startDate} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{e.name}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} /> {e.format}
                    </span>
                    {e.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> {e.location}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DateChip({ date }: { date: string }) {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return <span className="text-xs">{date}</span>;
  const dt = new Date(y, m - 1, d);
  const month = dt.toLocaleDateString(undefined, { month: "short" });
  return (
    <div className="rounded border border-border bg-background py-1 leading-none">
      <div className="text-[9px] font-display uppercase tracking-widest text-muted-foreground">
        {month}
      </div>
      <div className="text-lg font-display tabular-nums">{d}</div>
    </div>
  );
}
